import { Alert, Autocomplete, FormControl, Stack, TextField, Box, Typography } from '@mui/material'
import { useState, useCallback } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useDebounced } from '~/lib/hooks/useDebounced'
import { useCampaignNearestStationsLazyQuery } from '~/generated/graphql'
import { CreateCampaignState } from './state'
import { ValidationErrorMessage } from '~/components/common/ValidationErrorMessage'

type StationSearchProps = {
  stationSearchError: boolean
  setStationSearchError: (value: boolean) => void
}

export const StationSearch = ({ stationSearchError, setStationSearchError }: StationSearchProps) => {
  const { control, clearErrors, setError, setValue } = useFormContext<CreateCampaignState>()
  const [getCampaignNearestStation, { loading: loadingNearestStation }] = useCampaignNearestStationsLazyQuery()

  // 最寄り駅のAutocompleteのoptions
  const [stationOptions, setStationOptions] = useState<{ code: string; name: string; label?: string }[]>([])
  // 検索結果が0件かどうか
  const [noResults, setNoResults] = useState(false)

  // 駅名検索処理
  const searchStation = useCallback(
    async (stationName: string) => {
      if (!stationName.endsWith('駅')) {
        setError('nearestStationName', {
          type: 'manual',
          message: '最寄り駅は「駅」で終わる必要があります',
        })
        setStationOptions([])
        setStationSearchError(false)
        setNoResults(false)
        return
      }

      try {
        clearErrors('nearestStationName')
        setStationSearchError(false)
        setNoResults(false)
        const { data, error } = await getCampaignNearestStation({
          variables: { input: { stationName: stationName } },
        })

        if (error) {
          throw error
        }

        const stations = data?.campaignNearestStations ?? []

        if (stations.length === 0) {
          setNoResults(true)
          setStationOptions([])
          return
        }

        const options = stations.map((s) => ({ code: s.code, name: s.name }))
        setStationOptions(options)

        const autoSelectedStation = stations[0]
        setValue('nearestStationName', autoSelectedStation?.name)
      } catch {
        setStationSearchError(true)
        setNoResults(false)
        setStationOptions([])
      }
    },
    [getCampaignNearestStation, clearErrors, setError, setValue, setStationSearchError],
  )

  // useDebounced フックで500msのdebounceを適用
  const debouncedSearchStation = useDebounced(searchStation, 300)

  const handleStationInputChange = useCallback(
    (_: unknown, value: string) => {
      if (value.trim()) {
        debouncedSearchStation(value)
      }
    },
    [debouncedSearchStation],
  )

  // 自由入力を受け入れるかどうか
  const acceptsFreeInput = stationSearchError || noResults

  return (
    <FormControl fullWidth>
      <Stack gap={0.5}>
        {stationSearchError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            駅名の検索に失敗しました。お手数ですが、手動で入力してください。
          </Alert>
        )}
        <Controller
          name="nearestStationName"
          control={control}
          render={({ field, fieldState: { error } }) => {
            return (
              <Autocomplete
                value={field.value}
                options={stationOptions}
                fullWidth
                loading={loadingNearestStation}
                freeSolo
                selectOnFocus
                clearOnBlur
                autoSelect={acceptsFreeInput}
                handleHomeEndKeys
                onChange={(_, newValue) => {
                  if (typeof newValue !== 'string' && newValue && newValue.name) {
                    // オプションから選択した場合
                    field.onChange(newValue.name)
                  }
                }}
                onInputChange={(event, value, reason) => {
                  clearErrors('nearestStationName')
                  if (reason === 'clear') {
                    field.onChange('')
                    return
                  }
                  if (reason === 'input') {
                    field.onChange('')
                    // ユーザー入力による変更の場合のみ検索を実行
                    handleStationInputChange(event, value)
                  }
                }}
                filterOptions={(options, params) => {
                  const { inputValue } = params

                  // 自由入力のオプションを追加
                  if (acceptsFreeInput && inputValue !== '') {
                    const isExisting = options.some((option) => option.name === inputValue)
                    if (!isExisting) {
                      return [
                        ...options,
                        {
                          code: '',
                          name: inputValue,
                          label: `一致する駅が見つかりませんでした。駅名をご確認のうえ再度お試しください。\n※${inputValue}として登録もできますが、審査時に否認される可能性があります`,
                        },
                      ]
                    }
                  }

                  // APIから返されたoptionsをそのまま返す（フィルタリングしない）
                  return options
                }}
                getOptionLabel={(option) => {
                  // 文字列の場合はそのまま返す
                  if (typeof option === 'string') {
                    return option
                  }
                  // labelがある場合はlabelを返す（エラー時の警告メッセージ）
                  if (option.label) {
                    return option.label
                  }
                  // 通常はnameを返す
                  return option.name
                }}
                isOptionEqualToValue={(option, value) => option.name === value.name}
                renderOption={(props, option) => {
                  // labelがある場合（エラー時の警告メッセージ）は改行を含めて表示
                  if (option.label) {
                    const lines = option.label.split('\n')
                    return (
                      <Box component="li" {...props}>
                        <Box>
                          {lines.map((line, index) => (
                            <Typography key={index} variant="body2">
                              {line}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    )
                  }
                  // 通常のオプションはそのまま表示
                  return (
                    <Box component="li" {...props}>
                      {option.name}
                    </Box>
                  )
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="最寄り駅"
                    placeholder="渋谷駅"
                    error={!!error?.message}
                    helperText={<ValidationErrorMessage error={error?.message} />}
                    inputRef={field.ref}
                  />
                )}
              />
            )
          }}
        />
      </Stack>
    </FormControl>
  )
}
